import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { PageHeader, Pager, StatusBadge } from '@/components/kit';
import { FilterSelect, ListBody, SearchBox, Toolbar, useDebounced } from '@/components/inputs';
import DataTable, { Dash, RowLink } from '@/components/admin/DataTable';
import { CostCell, ProfitCell, useCostEditor, useOrderCosts } from '@/components/admin/OrderCosts';
import { ORDER_STATUSES, ORDER_STATUS_STYLES } from '@/shared/constants';
import { ORDER_LIST_FIELDS } from '@/shared/fields';
import { RENEW_WARN_DAYS, money, orderTotal, planSummary, timeLeft } from '@/shared/pricing';
import { paged } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

const { eq, or, gte, lte, lt, isIn, isNotNull, contains } = window.ApperSDK.CoreSDK;

export const route = { path: '/admin/orders', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };
export const nav = { icon: 'Package', label: 'Orders', section: 'Store', profiles: ['Admin_c', 'Staff_c'], order: 20 };

const PAGE_SIZE = 20;
const DAY = 86400000;
const iso = (d) => d.toISOString().slice(0, 19);
const VIEWS = ['expiring', 'lapsed', 'renewals'];
const VIEW_LABELS = { expiring: 'Ending in 7 days', lapsed: 'Lapsed', renewals: 'Renewal asked' };

/** "gc 7k2qxm", "GC7K2QXM" and "gc-7k2qxm" all search as GC-7K2QXM. */
function codeQuery(q) {
  const m = q.replace(/\s+/g, '').match(/^gc-?([a-z0-9]{6})$/i);
  return m ? `GC-${m[1].toUpperCase()}` : null;
}

function filtersFor(status, view, q) {
  const now = new Date();
  const f = [];
  if (status !== 'all') f.push(eq('status_c', status));
  if (view === 'expiring') {
    f.push(isIn('status_c', ['Active', 'Trial']));
    f.push(gte('ends_at_c', iso(now)));
    f.push(lte('ends_at_c', iso(new Date(now.getTime() + RENEW_WARN_DAYS * DAY))));
  }
  if (view === 'lapsed') {
    f.push(eq('status_c', 'Active'));
    f.push(lt('ends_at_c', iso(now)));
  }
  if (view === 'renewals') {
    f.push(isIn('status_c', ['Active', 'Trial']));
    f.push(isNotNull('renew_requested_at_c'));
  }
  if (q) {
    const code = codeQuery(q);
    f.push(
      code
        ? or(contains('Name', q), contains('Name', code), contains('email_c', q), contains('full_name_c', q))
        : or(contains('Name', q), contains('email_c', q), contains('full_name_c', q)),
    );
  }
  return f;
}

export default function AdminOrders() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') || 'all';
  const view = params.get('view') || 'all';
  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim());
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [status, view, q]);

  function setParam(key, value) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!value || value === 'all') next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  }

  const { data, loading, error, run } = useFetch(() => {
    let query = sdk.table('orders_c').select(ORDER_LIST_FIELDS);
    filtersFor(status, view, q).forEach((f) => {
      query = query.where(f);
    });
    query = view === 'expiring' ? query.orderBy('ends_at_c') : query.orderByDesc('CreatedOn');
    return query.page(page, PAGE_SIZE).fetch().then(paged);
  }, [status, view, q, page]);

  const list = data?.rows ?? [];
  const filtered = status !== 'all' || view !== 'all' || !!q;
  const costs = useOrderCosts(list.map((o) => o.Id));
  const [editCost, costDialog] = useCostEditor(() => costs.run());

  function clearFilters() {
    setSearch('');
    setParams({}, { replace: true });
  }

  const columns = [
    {
      label: 'Order',
      render: (o) => (
        <RowLink to={`/admin/orders/${o.Id}`} mono sub={o.full_name_c || o.customer_c?.Name || o.email_c}>
          {o.Name}
        </RowLink>
      ),
    },
    {
      label: 'Customer',
      className: 'hidden lg:table-cell',
      render: (o) => (
        <span className="block max-w-[16rem] truncate text-muted-foreground">{o.email_c || <Dash />}</span>
      ),
    },
    { label: 'Package', className: 'hidden md:table-cell', render: (o) => planSummary(o) },
    { label: 'Status', render: (o) => <StatusBadge value={o.status_c} styles={ORDER_STATUS_STYLES} /> },
    {
      label: 'Term ends',
      className: 'hidden md:table-cell',
      render: (o) =>
        o.ends_at_c ? (
          <span>
            {formatLocalDate(o.ends_at_c, DATE_FORMATS.SHORT)}
            <span className="block text-xs text-muted-foreground">
              {timeLeft(o.ends_at_c)}
              {o.renew_requested_at_c ? ' · renewal asked' : ''}
            </span>
          </span>
        ) : (
          <Dash />
        ),
    },
    {
      label: 'Placed',
      className: 'hidden xl:table-cell text-muted-foreground',
      render: (o) => formatLocalDate(o.CreatedOn, DATE_FORMATS.SHORT),
    },
    {
      label: 'Credits / cost',
      render: (o) => (
        <CostCell order={o} summary={costs.data?.[o.Id]} loading={costs.loading} onEdit={editCost} className="-ml-2" />
      ),
    },
    { label: 'Total', className: 'text-right tabular-nums', render: (o) => money(orderTotal(o)) },
    {
      label: 'Profit',
      className: 'text-right',
      render: (o) => <ProfitCell order={o} summary={costs.data?.[o.Id]} loading={costs.loading} />,
    },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow="Store"
        title="Orders"
        description="Every line, trial and licence, with its profit. Click credits or cost on any row to change them."
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/admin/orders/import">
                <ApperIcon name="Upload" size={16} />
                Import
              </Link>
            </Button>
            <Button asChild>
              <Link to="/admin/orders/new">
                <ApperIcon name="Plus" size={16} />
                New order
              </Link>
            </Button>
          </>
        }
      />

      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Search GC- code, name or email…" />
        <FilterSelect
          value={status}
          onChange={(v) => setParam('status', v)}
          values={ORDER_STATUSES}
          allLabel="All statuses"
        />
        <FilterSelect
          value={view}
          onChange={(v) => setParam('view', v)}
          values={VIEWS}
          labels={VIEW_LABELS}
          allLabel="Any term"
        />
        {filtered && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <ApperIcon name="X" size={16} />
            Clear
          </Button>
        )}
      </Toolbar>

      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        isEmpty={!list.length}
        empty={{
          icon: 'Package',
          title: filtered ? 'No orders match' : 'No orders yet',
          description: filtered
            ? 'Nothing fits these filters. Clear them to see every order.'
            : 'Orders from the checkout land here. You can also take one by hand.',
          action: filtered ? clearFilters : () => navigate('/admin/orders/new'),
          actionLabel: filtered ? 'Clear filters' : 'New order',
        }}
      >
        <DataTable columns={columns} rows={list} />
        <Pager page={page} total={data?.total} pageSize={PAGE_SIZE} hasMore={data?.hasMore} onPage={setPage} />
      </ListBody>
      {costDialog}
    </div>
  );
}
